import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import backarrow from "../../../../assets/images/back-arrow-left.svg";
import closeicon from "../../../../assets/images/close.svg";
import documentsicon from "../../../../assets/images/description.svg";
import warningIcon from "../../../../assets/images/warning_amber.svg";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../../hooks/useScreenResolution";
import useAmanFlow from "../../../../hooks/useAmanFlow";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";

const DocumentsReadyInfo = ({
  continueClickHandler,
  hide_back_btn,
  show_close_btn,
  onClose,
  customBackHandler,
  isFirstPage = false,
}) => {
  const translate = useLanguageTranslate();
  const { mobile } = useScreenResolution();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";
  const { handlePrevSubstep } = useAmanFlow();

  const [backPortalTarget, setBackPortalTarget] = useState(null);
  useEffect(() => {
    if (typeof document === "undefined") return;
    setBackPortalTarget(document.getElementById("aman-back-portal"));
  }, []);

  const handleBackClick = () => {
    if (customBackHandler) {
      customBackHandler();
    } else {
      handlePrevSubstep();
    }
  };

  const handleNavigationClick = () => {
    if (isFirstPage || show_close_btn) {
      if (onClose) onClose();
    } else {
      handleBackClick();
    }
  };

  const showNavBtn = !hide_back_btn || isFirstPage || show_close_btn;
  const showCloseIcon = isFirstPage || show_close_btn;
  const mobileNavBtn = showNavBtn ? (
    <div className="block md:hidden">
      <button
        type="button"
        className={`flex items-center justify-center w-10 h-10 fixed top-3 rounded-full bg-[#F6F8F9] hover:bg-gray-200 transition-colors z-[100] ${
          isRTL ? "right-4" : "left-4"
        }`}
        aria-label={
          showCloseIcon
            ? translate("Close", "إغلاق")
            : translate("Back", "رجوع")
        }
        onClick={handleNavigationClick}
      >
        {showCloseIcon ? (
          <img src={closeicon} alt="Close" className="w-5 h-5" />
        ) : (
          <img
            src={backarrow}
            alt="Back"
            className={`w-5 h-5 ${isRTL ? "rotate-180" : ""}`}
          />
        )}
      </button>
    </div>
  ) : null;

  const importantNotes = [
    {
      en: "Surgical procedures are subject to three months waiting period from policy inception date",
      ar: "إجراء العمليات الجراحية خاضع لفترة انتظار مدتها ثلاثة أشهر 3 أشهر من تاريخ بدء وثيقة التأمين.",
    },
    {
      en: "Treatment is only covered at NETWORK; Reimbursement is not allowed in all cases.",
      ar: "تُغطى العلاجات فقط في شبكة المستشفيات المعينة ولا يُسمح بالتعويض في جميع الحالات.",
    },
    {
      en: "The member must be first assessed by a General Practitioner and only \"if\" needed, will be referred to a Specialist or a Consultant. No direct access to Specialists.",
      ar: "يجب أن يتم تقييم المؤمن عليه أولاً من قبل طبيب عام، وإذا لزم الأمر، سيتم إحالته إلى أخصائي أو استشاري. لن نغطي العلاج لدى الأخصائيين دون تحويل من الطبيب العام.",
    },
    {
      en: "Only Generic Medicines and Basic Investigations are covered",
      ar: "تُغطى الأدوية العامة فقط والفحوصات الأساسية.",
    },
    {
      en: "Domestic helper must be between 18-59 years old.",
      ar: "يجب أن يكون العامل المنزلي بين 18-59 عامًا.",
    },
    {
      en: "No medical physical cards will be issued and the members can receive treatments using the ID cards or by downloading the e-card from Health 360° Ancillary Services mobile application.",
      ar: "لن يتم إصدار بطاقات طبية فعلية، ويمكن للأعضاء تلقي العلاجات باستخدام بطاقات الهوية أو عن طريق تنزيل البطاقة الإلكترونية من تطبيق Health 360.",
    },
    {
      en: "The premium for this plan starts at: BHD 84 per member (excluding VAT) for AMAN, and BHD 94 (excluding VAT) for AMAN Plus",
      ar: "تبدأ قسط هذا البرنامج من 84 دينار بحريني (باستثناء ضريبة القيمة المضافة) لكل عضو تحت بوليصة أمان و من 94 دينار بحريني (باستثناء ضريبة القيمة المضافة) تحت بوليصة أمان بلس",
    },
  ];

  return (
    <div className="documents-ready-info flex flex-col h-full w-full bg-white">
      {backPortalTarget
        ? createPortal(mobileNavBtn, backPortalTarget)
        : mobileNavBtn}
      {mobile && (
        <div className="px-5 pt-2 pb-4">
          <div className="flex flex-col gap-1 text-left rtl:text-right">
            <h1 className="text-[#1A2024] text-2xl font-semibold leading-8 tracking-[-0.24px]">
              {translate(
                "Ready to Start Your New Aman Policy?",
                "هل أنت مستعد لبدء وثيقة أمَانْ الجديدة الخاصة بك؟"
              )}
            </h1>
            <p className="text-[#1A2024] text-base font-normal leading-6 tracking-[-0.16px]">
              {translate(
                "Please ensure you have the following information and documents ready for smooth process.",
                "يرجى التأكد من أن لديك المعلومات والمستندات التالية جاهزة لضمان سير العملية بسلاسة."
              )}
            </p>
          </div>
        </div>
      )}

      {!mobile && (
        <div className="text-center pb-4">
          <h1 className="text-[#1A2024] text-2xl font-semibold leading-8 tracking-[-0.24px]">
            {translate(
              "Ready to Start Your New Aman Policy?",
              "هل أنت مستعد لبدء وثيقة أمَانْ الجديدة الخاصة بك؟"
            )}
          </h1>
          <p className="text-[#1A2024] text-base font-normal leading-6 tracking-[-0.16px]">
            {translate(
              "Please ensure you have the following information and documents ready for smooth process.",
              "يرجى التأكد من أن لديك المعلومات والمستندات التالية جاهزة لضمان سير العملية بسلاسة."
            )}
          </p>
        </div>
      )}

      <BottomStickyBtnLayout
        btn_label={translate("Continue", "استمرار")}
        btn_onClick={continueClickHandler}
        show_summary={false}
      >
        <div className="w-[500px] max767:w-full mx-auto ipad:w-[90%] flex flex-col">
          <div className="flex flex-col gap-4 max767:px-5 pb-32">
            <div className="bg-[#F6F8F9] rounded-2xl p-4 flex flex-col gap-2">
              <div className="flex gap-2 items-start">
                <img src={documentsicon} alt="" className="w-6 h-6" />
                <p className="text-[#1A2024] text-base font-semibold leading-6 tracking-[-0.16px]">
                  {translate("Documents", "المستندات")}
                </p>
              </div>
              <div className="ltr:pl-8 rtl:pr-8">
                <div className="flex gap-2 items-center py-1">
                  <span className="text-[#48535B] text-sm font-normal leading-5 tracking-[-0.14px]">
                    • {translate("CPR Bahraini National ID", "بطاقة الهوية الوطنية البحرينية CPR")}
                  </span>
                  <svg className="w-4 h-4 text-[#6E7C87] flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm0-9.5a.75.75 0 01.75.75v4a.75.75 0 01-1.5 0v-4A.75.75 0 018 5.5zm0-2a.75.75 0 100 1.5.75.75 0 000-1.5z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-[#F6F8F9] rounded-2xl p-4 flex flex-col gap-2">
              <div className="flex gap-2 items-start">
                <img src={warningIcon} alt="" className="w-6 h-6" />
                <p className="text-[#1A2024] text-base font-semibold leading-6 tracking-[-0.16px]">
                  {translate("Important Notes", "ملاحظات مهمة")}
                </p>
              </div>
              <div className="px-6">
                <ul className="text-[#48535B] text-sm font-normal leading-[22px] tracking-[-0.14px] list-disc ltr:ml-[21px] rtl:mr-[21px]">
                  {importantNotes.map((note, index) => (
                    <li key={index} className="py-1">{translate(note.en, note.ar)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </BottomStickyBtnLayout>
    </div>
  );
};

export default DocumentsReadyInfo;
